/*
 * Inline SVG icon set (2026-07-21 polish pass) — replaces the unicode-glyph
 * nav icons (▦ ◈ ⚑ …) whose rendering varied by OS font. Hand-drawn 16px,
 * 1.5px stroke, currentColor — zero dependencies, tree-shaken, ~100 bytes
 * each. The BrandMark is the product identity: an ascending launch trajectory
 * inside a rounded tile, echoed by the favicon and the login hero.
 */
import { useId } from "react";
import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function base({ size = 16, ...rest }: IconProps): SVGProps<SVGSVGElement> {
  return {
    width: size,
    height: size,
    viewBox: "0 0 16 16",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    focusable: false,
    ...rest,
  };
}

/** The product mark: launch trajectory on the brand-gradient tile
 *  (indigo → violet → cyan, 2026-07-23 colour pass). Gradient id is
 *  useId-scoped so multiple marks per page never collide. */
export function BrandMark({ size = 22, ...rest }: IconProps) {
  const gid = useId();
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden
      focusable={false}
      {...rest}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
          <stop stopColor="#5457e6" />
          <stop offset="0.55" stopColor="#7c5cf0" />
          <stop offset="1" stopColor="#22b8cf" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="8" fill={`url(#${gid})`} />
      <path
        d="M7 23c6.5-1 12-6.5 13-13"
        stroke="#fff"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M15.5 9.5 20 10l.5 4.5"
        stroke="#fff"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="24" r="1.6" fill="#fff" />
    </svg>
  );
}

/** Queue — stacked rows with a leading marker. */
export function IconQueue(p: IconProps) {
  return (
    <svg {...base(p)}>
      <path d="M5.5 4h8M5.5 8h8M5.5 12h8" />
      <path d="M2.5 4h.01M2.5 8h.01M2.5 12h.01" strokeWidth="2.2" />
    </svg>
  );
}

/** Products — an isometric box. */
export function IconProducts(p: IconProps) {
  return (
    <svg {...base(p)}>
      <path d="M8 1.8 14 5v6L8 14.2 2 11V5l6-3.2Z" />
      <path d="M2 5l6 3.2L14 5M8 8.2v6" />
    </svg>
  );
}

/** Hygiene — shield + check. */
export function IconHygiene(p: IconProps) {
  return (
    <svg {...base(p)}>
      <path d="M8 1.8 13.5 4v4.1c0 3.2-2.3 5.3-5.5 6.3-3.2-1-5.5-3.1-5.5-6.3V4L8 1.8Z" />
      <path d="m5.6 8 1.7 1.7L10.6 6.4" />
    </svg>
  );
}

/** Video tickets — a play triangle in a frame. */
export function IconVideo(p: IconProps) {
  return (
    <svg {...base(p)}>
      <rect x="1.8" y="3" width="12.4" height="10" rx="1.6" />
      <path d="M6.6 5.9v4.2L10.2 8 6.6 5.9Z" />
    </svg>
  );
}

/** Performance — ascending bars. */
export function IconPerformance(p: IconProps) {
  return (
    <svg {...base(p)}>
      <path d="M3 13.5V10M8 13.5V6.5M13 13.5V3" />
    </svg>
  );
}

/** Team — two figures. */
export function IconTeam(p: IconProps) {
  return (
    <svg {...base(p)}>
      <circle cx="6" cy="5.5" r="2.4" />
      <path d="M1.8 13.5c.5-2.6 2.2-4 4.2-4s3.7 1.4 4.2 4" />
      <path d="M11 3.6a2.4 2.4 0 0 1 0 3.8M12.4 9.8c1.1.6 1.7 1.9 2 3.7" />
    </svg>
  );
}

/** Credentials — key. */
export function IconCredentials(p: IconProps) {
  return (
    <svg {...base(p)}>
      <circle cx="5" cy="5" r="3.2" />
      <path d="m7.3 7.3 6.2 6.2M11 11l1.8-1.8M13 13l1.2-1.2" />
    </svg>
  );
}

/** Users — global roster. */
export function IconUsers(p: IconProps) {
  return (
    <svg {...base(p)}>
      <circle cx="8" cy="5" r="2.6" />
      <path d="M2.8 13.8c.6-3 2.7-4.6 5.2-4.6s4.6 1.6 5.2 4.6" />
    </svg>
  );
}

/** Brands — tiles. */
export function IconBrands(p: IconProps) {
  return (
    <svg {...base(p)}>
      <rect x="2.2" y="2.2" width="5" height="5" rx="1.2" />
      <rect x="8.8" y="2.2" width="5" height="5" rx="1.2" />
      <rect x="2.2" y="8.8" width="5" height="5" rx="1.2" />
      <rect x="8.8" y="8.8" width="5" height="5" rx="1.2" />
    </svg>
  );
}
